import React, {useCallback, useEffect, useRef, useState} from "react";
import {zodResolver} from '@hookform/resolvers/zod';
import {useForm} from 'react-hook-form';
import {z} from 'zod';
import {Form, FormControl, FormField, FormItem, FormLabel, FormMessage} from '@/components/ui/form';
import {Select, SelectContent, SelectItem, SelectTrigger, SelectValue} from "@/components/ui/select";
import {useRouter} from "next/router";
import {Button} from "@/components/ui/button";
import {ButtonGroup, ButtonGroupItem} from "@/components/CustomRadioGroup";
import {Input} from "@/components/ui/input";
import {Textarea} from "@/components/ui/textarea";
import {provinces} from "@/components/FormLead";
import {useDropzone} from "react-dropzone";
import {AlertCircle, ChevronRight, Loader, Upload, X} from "lucide-react";
import Image from "next/image";
import {CardDescription} from "@/components/ui/card";
import {Alert, AlertDescription, AlertTitle} from "@/components/ui/alert";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle
} from "@/components/ui/dialog";

// Tipi
interface DettagliGenerali {
    marca: string;
    anno: string;
    mese: string;
    modello: string;
    versione: string;
    email: string;
    chilometri: string;
    gpic: string;
    idUtente: string;
}

// Schema di validazione
const schemaDettagliGenerali = z.object({
    marca: z.string().min(1, {message: 'La marca è obbligatoria.'}),
    anno: z.string().min(1, {message: 'L\'anno è obbligatorio.'}),
    mese: z.string().min(1, {message: 'Il mese è obbligatorio.'}),
    modello: z.string().min(1, {message: 'Il modello è obbligatorio.'}),
    versione: z.string().min(1, {message: 'La versione è obbligatoria.'}),
    email: z.string().email({message: 'L\'email non è valida.'}),
    chilometri: z.string().min(1, {message: 'I chilometri sono obbligatori.'}).regex(/^[0-9]*$/, {message: 'I chilometri devono essere un numero.'}),
    permuta: z.string().min(1, {message: 'La scelta è obbligatoria.'}),
});

// Tipi
interface RichiesteVeicolo {
    reqBollo: string;
    reqNChiavi: string;
    reqSetGomme: string;
    reqLibroMan: string;
    reqRevisione: string;
    reqPrivato: string;
    reqImportata: string;
    reqColoreEsterno: string;
    reqColoreSedili: string;
    reqMaterialeSedili: string;
}

// Schema di validazione
const schemaRichiesteVeicolo = z.object({
    reqBollo: z.string().min(1, {message: 'Il bollo è obbligatorio.'}),
    reqNChiavi: z.string().min(1, {message: 'Il numero di chiavi è obbligatorio.'}),
    reqSetGomme: z.string().min(1, {message: 'Il set di gomme è obbligatorio.'}),
    reqLibroMan: z.string().min(1, {message: 'Il libro manutenzione è obbligatorio.'}),
    reqRevisione: z.string().min(1, {message: 'La revisione è obbligatoria.'}),
    reqPrivato: z.string().min(1, {message: 'Il privato è obbligatorio.'}),
    reqImportata: z.string().min(1, {message: 'L\'importazione è obbligatoria.'}),
    reqColoreEsterno: z.string().min(1, {message: 'Il colore esterno è obbligatorio.'}),
    reqColoreSedili: z.string().min(1, {message: 'Il colore dei sedili è obbligatorio.'}),
    reqMaterialeSedili: z.string().min(1, {message: 'Il materiale dei sedili è obbligatorio.'}),
});


// Tipi
interface CondizioniVeicolo {
    reqAntSin?: string;
    reqAssLiquid?: string;
    reqIncidenti: string;
    reqRiparati?: string;
    reqInterni: string;
    reqEsterni: string;
    reqSpie: string;
    reqUsura: string;
    reqMotore: string;
    reqCambio: string;
    reqSterzo?: string;
    reqSospensioni?: string;
    reqFreni?: string;
    reqFrizione: string;
    reqCondizionatore: string;
}

// Schema di validazione
const schemaCondizioniVeicolo = z.object({
    reqIncidenti: z.string().min(1, {message: 'Gli incidenti sono obbligatori.'}),
    reqInterni: z.string().min(1, {message: 'Gli interni sono obbligatori.'}),
    reqEsterni: z.string().min(1, {message: 'Gli esterni sono obbligatori.'}),
    reqSpie: z.string().min(1, {message: 'Le spie sono obbligatorie.'}),
    reqUsura: z.string().min(1, {message: 'L\'usura è obbligatoria.'}),
    reqMotore: z.string().min(1, {message: 'Il motore è obbligatorio.'}),
    reqCambio: z.string().min(1, {message: 'Il cambio è obbligatorio.'}),
    reqFrizione: z.string().min(1, {message: 'La frizione è obbligatoria.'}),
    reqCondizionatore: z.string().min(1, {message: 'Il condizionatore è obbligatorio.'}),
});

// Tipi
interface DatiCliente {
    valutazione: string;
    nome: string;
    cognome: string;
    targa: string;
    telefono: string;
    foto?: string;
    provincia: string;
    note?: string;
}

// Schema di validazione
const schemaDatiCliente = z.object({
    valutazione: z.string().min(1, {message: 'La valutazione richiesta è obbligatoria.'}),
    nome: z.string().min(1, {message: 'Il nome è obbligatorio.'}),
    cognome: z.string().min(1, {message: 'Il cognome è obbligatorio.'}),
    targa: z.string().min(7, {message: 'La targa deve essere di 7 caratteri.'}).max(7, {message: 'La targa deve essere di 7 caratteri.'}).regex(/^[A-Za-z]{2}\d{3}[A-Za-z]{2}$/, {message: 'La targa non è valida.'}),
    telefono: z.string().min(10, {message: 'Il telefono deve essere di 10 cifre.'}).max(10, {message: 'Il telefono deve essere di 10 cifre.'}).regex(/^[0-9]*$/, {message: 'Il telefono deve essere un numero.'}),
    provincia: z.string().min(1, {message: 'La provincia è obbligatoria.'}),
    note: z.string().optional(),
});


interface FormPermutaProps {
    title?: string;
}

const FormPermuta: React.FC = ({title = 'Inserisci i dati della tua auto'}: FormPermutaProps) => {
    const router = useRouter();
    const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
    const isSubmittingRef = useRef(false);
    const [currentStep, setCurrentStep] = useState<number>(1);
    const [openDialog, setOpenDialog] = useState(false);
    const [dialogMessage, setDialogMessage] = useState("");
    const [photos, setPhotos] = useState<{ file: File, url: string | null, isLoading: boolean }[]>([]);

    const [valutezione, setValutezione] = useState<{ min: number; max: number } | null>(null);

    const [productId, setProductId] = useState<string>("");
    const [permutaId, setPermutaId] = useState<string>("");

    const uploadPhoto = async (file: File) => {
        const formData = new FormData();
        formData.append("file", file);

        try {
            const response = await fetch(`${process.env.NEXT_PUBLIC_API_URL_ASTA}/api/v1/store/upload-file`, {
                method: "POST",
                body: formData,
            });

            const data = await response.json();
            return data.url;
        } catch (error) {
            console.error("Errore nel caricamento della foto", error);
            return null;
        }
    };

    const onDrop = useCallback(async (acceptedFiles: File[]) => {
        const newPhotos = acceptedFiles.map(file => ({
            file,
            url: null,
            isLoading: true
        }));

        setPhotos(prevPhotos => [...prevPhotos, ...newPhotos]);

        const uploadedPhotos = await Promise.all(
            newPhotos.map(async (photo) => {
                const url = await uploadPhoto(photo.file);
                return { ...photo, url, isLoading: false };
            })
        );

        setPhotos(prevPhotos =>
            prevPhotos.map(photo => uploadedPhotos.find(p => p.file === photo.file) || photo)
        );
    }, []);

    const removePhoto = (index: number) => {
        setPhotos((prevPhotos) => prevPhotos.filter((_, i) => i !== index));
    };

    const { getRootProps, getInputProps, isDragActive } = useDropzone({
        onDrop,
        accept: { "image/*": [] },
        maxFiles: 20,
    });

    const [anni, setAnni] = useState<string[]>([]);
    const [mesi, setMesi] = useState<{ value: string; label: string; }[]>([]);
    const [modelli, setModelli] = useState<any[]>([]);
    const [versioni, setVersioni] = useState<any[]>([]);
    const [marche, setMarche] = useState<any[]>([]);
    const minNumberOfPhotos = 5;

    const formDettagliGenerali = useForm({
        resolver: zodResolver(schemaDettagliGenerali),
        mode: "onTouched",
        defaultValues: {
            marca: '',
            anno: '',
            mese: '',
            modello: '',
            versione: '',
            email: '',
            chilometri: '',
            permuta: '',
        }
    });

    const formRichiesteVeicolo = useForm({
        resolver: zodResolver(schemaRichiesteVeicolo),
        mode: "onTouched",
        defaultValues: {
            reqBollo: '',
            reqNChiavi: '',
            reqSetGomme: '',
            reqLibroMan: '',
            reqRevisione: '',
            reqPrivato: '',
            reqImportata: '',
            reqColoreEsterno: '',
            reqColoreSedili: '',
            reqMaterialeSedili: '',
        }
    });

    const formCondizioniVeicolo = useForm({
        resolver: zodResolver(schemaCondizioniVeicolo),
        mode: "onTouched",
        defaultValues: {
            reqIncidenti: '',
            reqInterni: '',
            reqEsterni: '',
            reqSpie: '',
            reqUsura: '',
            reqMotore: '',
            reqCambio: '',
            reqFrizione: '',
            reqCondizionatore: '',
        }
    });

    const formDatiCliente = useForm({
        resolver: zodResolver(schemaDatiCliente),
        mode: "onTouched",
        defaultValues: {
            valutazione: '',
            nome: '',
            cognome: '',
            targa: '',
            telefono: '',
            provincia: '',
            note: '',
        }
    });

    const onSubmit = async () => {

        if (isSubmittingRef.current || isSubmitting) return; // Blocca invii multipli
        isSubmittingRef.current = true;
        setIsSubmitting(true);

        try {

            if (photos.length > 0) {
                if (photos.some(photo => photo.isLoading)) {
                    console.log("Attenzione: ci sono foto in caricamento");
                    isSubmittingRef.current = false;
                    setIsSubmitting(false);
                    return;
                }

                const params = {
                    productId: productId,
                    photos: photos.map((photo) => ({ url: photo.url })),
                };

                const response = await fetch(`${process.env.NEXT_PUBLIC_API_URL_ASTA}/api/v1/store/update-permuta/${permutaId}`, {
                    method: 'PUT',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(params),
                });

                if (!response.ok) {
                    const errorData = await response.json();
                    if (response.status === 409) {
                        setDialogMessage("Hai già inviato una permuta recentemente. Attendi prima di inviare nuovamente.");
                        setOpenDialog(true);
                        isSubmittingRef.current = false;
                        setIsSubmitting(false);
                        return;
                    }
                    throw new Error(errorData.message || "Errore nella risposta del server");
                }
            }



            const email = formDettagliGenerali.getValues().email;
            const nome = formDatiCliente.getValues().nome;
            const cognome = formDatiCliente.getValues().cognome;
            const telefono = formDatiCliente.getValues().telefono;

            // Reset dei form in caso di successo
            formDettagliGenerali.reset();
            formRichiesteVeicolo.reset();
            formCondizioniVeicolo.reset();
            formDatiCliente.reset();
            await router.push(`/thank-you-page/vendi-la-tua-auto?email=${encodeURIComponent(email)}&nome=${encodeURIComponent(nome)}&telefono=${encodeURIComponent(telefono)}`);

        } catch (error) {
            console.error("Errore durante la richiesta:", error);

            // Type assertion per TypeScript
            const errorMessage = error instanceof Error ? error.message : "Si è verificato un errore, riprova più tardi.";

            setDialogMessage(errorMessage);
            setOpenDialog(true);
        } finally {
            isSubmittingRef.current = false;
            setIsSubmitting(false);
        }
    };

    // Fetch per le marche
    useEffect(() => {
        const fetchData = async () => {
            try {
                const response = await fetch(`${process.env.NEXT_PUBLIC_API_URL}/api/eurotax/makes`);
                if (!response.ok) {
                    throw new Error('Errore nella risposta del server');
                }
                const data = await response.json();
                setMarche(data.marche);
                setMesi([]);
                setAnni([]);
                setModelli([]);
                setVersioni([]);
            } catch (error) {
                console.error("Error fetching data:", error);
            }
        };
        fetchData();
    }, []);

    // Effetti per aggiornare i valori dipendenti
    useEffect(() => {
        const fetchYears = async () => {
            if (formDettagliGenerali.watch('marca')) {
                try {
                    const response = await fetch(`${process.env.NEXT_PUBLIC_API_URL}/api/eurotax/years/${formDettagliGenerali.watch('marca')}`);
                    if (!response.ok) {
                        throw new Error('Errore nella risposta del server');
                    }
                    const data = await response.json();
                    setAnni(data.anni.map(String));
                    formDettagliGenerali.reset({
                        ...formDettagliGenerali.getValues(),
                        anno: '',
                        mese: '',
                        modello: '',
                        versione: '',
                    });
                    setMesi([
                        {value: '1', label: 'Gennaio'},
                        {value: '2', label: 'Febbraio'},
                        {value: '3', label: 'Marzo'},
                        {value: '4', label: 'Aprile'},
                        {value: '5', label: 'Maggio'},
                        {value: '6', label: 'Giugno'},
                        {value: '7', label: 'Luglio'},
                        {value: '8', label: 'Agosto'},
                        {value: '9', label: 'Settembre'},
                        {value: '10', label: 'Ottobre'},
                        {value: '11', label: 'Novembre'},
                        {value: '12', label: 'Dicembre'},
                    ])
                    setModelli([]);
                    setVersioni([]);
                } catch (error) {
                    console.error("Error fetching years:", error);
                }
            }
        };
        fetchYears();
    }, [formDettagliGenerali.watch('marca')]);

    useEffect(() => {
        const fetchModels = async () => {
            if (formDettagliGenerali.watch('anno')) {
                try {
                    const response = await fetch(`${process.env.NEXT_PUBLIC_API_URL}/api/eurotax/models/${formDettagliGenerali.watch('marca')}/${formDettagliGenerali.watch('anno')}`);
                    if (!response.ok) {
                        throw new Error('Errore nella risposta del server');
                    }
                    const data = await response.json();
                    formDettagliGenerali.reset({
                        ...formDettagliGenerali.getValues(),
                        modello: '',
                        versione: '',
                    });
                    setModelli(data.modelli);
                    setVersioni([]);
                } catch (error) {
                    console.error("Error fetching models:", error);
                }
            }
        };
        fetchModels();
    }, [formDettagliGenerali.watch('anno')]);

    useEffect(() => {
        const fetchVersions = async () => {
            if (formDettagliGenerali.watch('modello')) {
                try {
                    const response = await fetch(`${process.env.NEXT_PUBLIC_API_URL}/api/eurotax/versions/${formDettagliGenerali.watch('modello')}/${formDettagliGenerali.watch('anno')}`);
                    if (!response.ok) {
                        throw new Error('Errore nella risposta del server');
                    }
                    const data = await response.json();
                    formDettagliGenerali.reset({
                        ...formDettagliGenerali.getValues(),
                        versione: '',
                    });
                    setVersioni(data.versioni);
                } catch (error) {
                    console.error("Error fetching versions:", error);
                }
            }
        };
        fetchVersions();
    }, [formDettagliGenerali.watch('modello')]);

    const stepNames: Record<number, string> = {
        1: "dettagli-generali",
        2: "richieste-veicolo",
        3: "condizioni-veicolo",
        4: "dati-cliente",
        5: "foto-auto",
    };


// Funzioni di validazione per ogni step
    const formValidators: Record<number, () => Promise<boolean>> = {
        1: () => formDettagliGenerali.trigger(),
        2: () => formRichiesteVeicolo.trigger(),
        3: () => formCondizioniVeicolo.trigger(),
        4: () => formDatiCliente.trigger(),
    };

    const sendPermuta = async () => {
        // router.push(`/thank-you-page/vendi-la-tua-auto?email=${encodeURIComponent(email)}&nome=${encodeURIComponent(nome)}&telefono=${encodeURIComponent(telefono)}`);

        if (isSubmittingRef.current || isSubmitting) return; // Blocca invii multipli
        isSubmittingRef.current = true;
        setIsSubmitting(true);

        try {
            const queryParams = new URLSearchParams(window.location.search);
            const gpic = queryParams.get('gpic') === 'true';
            const idUtente = queryParams.get('idUtente') || '';

            if (photos.some(photo => photo.isLoading)) {
                console.log("Attenzione: ci sono foto in caricamento");
                isSubmittingRef.current = false;
                setIsSubmitting(false);
                return;
            }

            const params = {
                step1: {
                    ...formDettagliGenerali.getValues(),
                    fonte: sessionStorage.getItem('source_t') || 'WEB',
                    gpic,
                    idUtente,
                    marcaDesc: marche.find(marca => marca.acronimo === formDettagliGenerali.getValues().marca)?.nome || '',
                    modelloDesc: modelli.find(modello => modello.gammaModello.codice === formDettagliGenerali.getValues().modello)?.gammaModello?.descrizione || '',
                    versioneDesc: versioni.find(versione => versione.codiceMotornetUnivoco === formDettagliGenerali.getValues().versione)?.nome || '',
                },
                step2: { ...formRichiesteVeicolo.getValues() },
                step3: { ...formCondizioniVeicolo.getValues() },
                step4: { ...formDatiCliente.getValues() },
                photos: photos.map((photo) => ({ url: photo.url })),
            };

            const response = await fetch(`${process.env.NEXT_PUBLIC_API_URL_ASTA}/api/v1/store/send-permuta`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(params),
            });

            if (!response.ok) {
                const errorData = await response.json();
                if (response.status === 409) {
                    setDialogMessage("Hai già inviato una permuta recentemente. Attendi prima di inviare nuovamente.");
                    setOpenDialog(true);
                    isSubmittingRef.current = false;
                    setIsSubmitting(false);
                    return;
                }
                throw new Error(errorData.message || "Errore nella risposta del server");
            } else {
                const data = await response.json();
                setProductId(data.data.productId);
                setPermutaId(data.data.idPermuta);
                setValutezione(data.data.valutazioneStimata);
            }

            const email = formDettagliGenerali.getValues().email;
            const nome = formDatiCliente.getValues().nome;
            const cognome = formDatiCliente.getValues().cognome;
            const telefono = formDatiCliente.getValues().telefono;

            // Reset dei form in caso di successo
            //formDettagliGenerali.reset();
            //formRichiesteVeicolo.reset();
            //formCondizioniVeicolo.reset();
            //formDatiCliente.reset();

        } catch (error) {
            console.error("Errore durante la richiesta:", error);

            // Type assertion per TypeScript
            const errorMessage = error instanceof Error ? error.message : "Si è verificato un errore, riprova più tardi.";

            setDialogMessage(errorMessage);
            setOpenDialog(true);
        } finally {
            isSubmittingRef.current = false;
            setIsSubmitting(false);
        }
    }


    const nextStep = async () => {
        if (formValidators[currentStep]) {
            const isValid = await formValidators[currentStep]();
            if (!isValid) return; // Ferma il passaggio se il form non è valido
        }

        if (currentStep === 4) {
            await sendPermuta();
        }

        setCurrentStep((prev) => {
            const newStep = Math.min(prev + 1, 5);

            // Aggiorna l'URL con il nuovo step
            const stepName = stepNames[newStep] || "foto-auto";
            const newUrl = new URL(window.location.href);
            newUrl.searchParams.set("step", stepName);
            window.history.pushState({}, "", newUrl.toString());

            // Scrolla all'inizio della pagina
            window.scrollTo({ top: 0, behavior: "smooth" });

            return newStep;
        });
    };


    const prevStep = () => setCurrentStep((prev) => Math.max(prev - 1, 1));

    const [step, setStep] = useState(1);

    const marca = formDettagliGenerali.watch("marca");
    const anno = formDettagliGenerali.watch("anno");
    const mese = formDettagliGenerali.watch("mese");
    const modello = formDettagliGenerali.watch("modello");
    const versione = formDettagliGenerali.watch("versione");
    const chilometri = formDettagliGenerali.watch("chilometri");
    const permuta = formDettagliGenerali.watch("permuta");
    const email = formDettagliGenerali.watch("email");

    useEffect(() => {
        if (marca) setStep(1);
        if (anno) setStep(2);
        if (mese) setStep(3);
        if (modello) setStep(4);
        if (versione) setStep(5);
        if (chilometri) setStep(6);
        if (permuta) setStep(7);
        if (email) setStep(8);
    }, [marca, anno, mese, modello, versione, chilometri, permuta, email]);

    return (
        <section id="form-permuta" className="bg-white/90  rounded-lg">


            {/*
            <h5 className="text-xl font-bold pb-5">{title}</h5>
*/}
            {currentStep === 1 && (
                <>
                    <Form {...formDettagliGenerali} >
                        <form id="formDettagliGenerali" onSubmit={formDettagliGenerali.handleSubmit(onSubmit)}
                              className="flex flex-col gap-4">

                            {/* Marca */}
                            {step >= 1 && (
                                <FormField control={formDettagliGenerali.control} name="marca"
                                           render={({field, fieldState}) => (
                                               <FormItem>
                                                   <FormLabel>Di che marca è la tua auto?</FormLabel>
                                                   <FormControl>
                                                       <Select {...field} onValueChange={field.onChange}
                                                               value={field.value}>
                                                           <SelectTrigger>
                                                               <SelectValue placeholder="Seleziona la marca"/>
                                                           </SelectTrigger>
                                                           <SelectContent>
                                                               {marche.map((marca, index) => (
                                                                   <SelectItem key={index}
                                                                               value={marca.acronimo}>{marca.nome}</SelectItem>
                                                               ))}
                                                           </SelectContent>
                                                       </Select>
                                                   </FormControl>
                                                   <FormMessage>{fieldState.error?.message}</FormMessage>
                                               </FormItem>
                                           )}
                                />
                            )}

                            {/* Anno */}
                            {step >= 1 && (
                                <FormField control={formDettagliGenerali.control} name="anno"
                                           render={({field, fieldState}) => (
                                               <FormItem>
                                                   <FormLabel>In che anno è stata immatricolata?</FormLabel>
                                                   <FormControl>
                                                       <Select {...field} onValueChange={field.onChange}
                                                               value={field.value} disabled={!marca}>
                                                           <SelectTrigger>
                                                               <SelectValue placeholder="Seleziona anno"/>
                                                           </SelectTrigger>
                                                           <SelectContent>
                                                               {anni.map((anno) => (
                                                                   <SelectItem key={anno}
                                                                               value={anno}>{anno}</SelectItem>
                                                               ))}
                                                           </SelectContent>
                                                       </Select>
                                                   </FormControl>
                                                   <FormMessage>{fieldState.error?.message}</FormMessage>
                                               </FormItem>
                                           )}
                                />
                            )}

                            {/* Mese */}
                            {step >= 1 && (
                                <FormField control={formDettagliGenerali.control} name="mese"
                                           render={({field, fieldState}) => (
                                               <FormItem>
                                                   <FormLabel>In che mese è stata immatricolata?</FormLabel>
                                                   <FormControl>
                                                       <Select {...field} onValueChange={field.onChange}
                                                               value={field.value} disabled={!anno}>
                                                           <SelectTrigger>
                                                               <SelectValue placeholder="Seleziona mese"/>
                                                           </SelectTrigger>
                                                           <SelectContent>
                                                               {mesi.map((mese, index) => (
                                                                   <SelectItem key={index}
                                                                               value={mese.value}>{mese.label}</SelectItem>
                                                               ))}
                                                           </SelectContent>
                                                       </Select>
                                                   </FormControl>
                                                   <FormMessage>{fieldState.error?.message}</FormMessage>
                                               </FormItem>
                                           )}
                                />
                            )}

                            {/* Modello */}
                            {step >= 3 && (
                                <FormField control={formDettagliGenerali.control} name="modello"
                                           render={({field, fieldState}) => (
                                               <FormItem>
                                                   <FormLabel>Qual è il modello?</FormLabel>
                                                   <FormControl>
                                                       <Select {...field} onValueChange={field.onChange}
                                                               value={field.value} disabled={!mese}>
                                                           <SelectTrigger>
                                                               <SelectValue placeholder="Seleziona modello"/>
                                                           </SelectTrigger>
                                                           <SelectContent>
                                                               {modelli.map((modello, index) => (
                                                                   <SelectItem key={index}
                                                                               value={modello.gammaModello.codice}>{modello.gammaModello.descrizione}</SelectItem>
                                                               ))}
                                                           </SelectContent>
                                                       </Select>
                                                   </FormControl>
                                                   <FormMessage>{fieldState.error?.message}</FormMessage>
                                               </FormItem>
                                           )}
                                />
                            )}

                            {/* Versione */}
                            {step >= 4 && (
                                <FormField control={formDettagliGenerali.control} name="versione"
                                           render={({field, fieldState}) => (
                                               <FormItem>
                                                   <FormLabel>Qual è la versione?</FormLabel>
                                                   <FormControl>
                                                       <Select {...field} onValueChange={field.onChange}
                                                               value={field.value} disabled={!modello}>
                                                           <SelectTrigger>
                                                               <SelectValue placeholder="Seleziona versione"/>
                                                           </SelectTrigger>
                                                           <SelectContent>
                                                               {versioni.map((versione, index) => (
                                                                   <SelectItem key={index}
                                                                               value={versione.codiceMotornetUnivoco}>{versione.nome}</SelectItem>
                                                               ))}
                                                           </SelectContent>
                                                       </Select>
                                                   </FormControl>
                                                   <FormMessage>{fieldState.error?.message}</FormMessage>
                                               </FormItem>
                                           )}
                                />
                            )}

                            {/* Chilometri */}
                            {step >= 5 && (
                                <FormField control={formDettagliGenerali.control} name="chilometri"
                                           render={({field, fieldState}) => (
                                               <FormItem>
                                                   <FormLabel>Quanti chilometri hai percorsi</FormLabel>
                                                   <FormControl>
                                                       <Input {...field} placeholder="Inserisci i chilometri"
                                                              disabled={!versione}/>
                                                   </FormControl>
                                                   <FormMessage>{fieldState.error?.message}</FormMessage>
                                               </FormItem>
                                           )}
                                />
                            )}

                            {/* Scelta Permuta */}
                            {step >= 6 && (
                                <FormField control={formDettagliGenerali.control} name="permuta"
                                           render={({field, fieldState}) => (
                                               <FormItem>
                                                   <FormLabel>Sei interessato alla permuta?</FormLabel>
                                                   <FormControl>
                                                       <ButtonGroup ref={field.ref} value={field.value}
                                                                    onValueChange={field.onChange}
                                                                    disabled={!chilometri}>
                                                           <ButtonGroupItem className="w-full justify-between px-3"
                                                                            value="Permuta"
                                                                            label="Si, voglio permutare la mia auto"/>
                                                           <ButtonGroupItem className="w-full justify-between px-3"
                                                                            value="Vendere"
                                                                            label="No, voglio solo vendere la mia auto"/>
                                                       </ButtonGroup>
                                                   </FormControl>
                                                   <FormMessage>{fieldState.error?.message}</FormMessage>
                                               </FormItem>
                                           )}
                                />
                            )}

                            {/* Email */}
                            {step >= 7 && (
                                <FormField control={formDettagliGenerali.control} name="email"
                                           render={({field, fieldState}) => (
                                               <FormItem>
                                                   <FormLabel>E-mail</FormLabel>
                                                   <FormControl>
                                                       <Input {...field} type="email"
                                                              placeholder="Inserisci la tua e-mail"
                                                              disabled={!permuta}/>
                                                   </FormControl>
                                                   <FormMessage>{fieldState.error?.message}</FormMessage>
                                               </FormItem>
                                           )}
                                />
                            )}

                        </form>
                    </Form>
                </>
            )}

            {currentStep === 2 && (
                <>
                    <Form {...formRichiesteVeicolo}>
                        <form id="formRichiesteVeicolo" onSubmit={formRichiesteVeicolo.handleSubmit(onSubmit)}
                              className="grid grid-cols-1 gap-4">

                            {/* Bollo pagato */}
                            <FormField control={formRichiesteVeicolo.control} name="reqBollo"
                                       render={({field, fieldState}) => (
                                           <FormItem>
                                               <FormLabel>Bollo pagato?</FormLabel>
                                               <FormControl>
                                                   <ButtonGroup ref={field.ref} value={field.value}
                                                                onValueChange={(value) => field.onChange(value)}>
                                                       <ButtonGroupItem className="w-full justify-between px-3"
                                                                        value="Si" label="Si"/>
                                                       <ButtonGroupItem className="w-full justify-between px-3"
                                                                        value="No" label="No"/>
                                                   </ButtonGroup>
                                               </FormControl>
                                               <FormMessage>{fieldState.error?.message}</FormMessage>
                                           </FormItem>
                                       )}
                            />

                            {/* Quante chiavi possiedi? */}
                            <FormField control={formRichiesteVeicolo.control} name="reqNChiavi"
                                       render={({field, fieldState}) => (
                                           <FormItem>
                                               <FormLabel>Quante chiavi possiedi?</FormLabel>
                                               <FormControl>
                                                   <ButtonGroup ref={field.ref} value={field.value}
                                                                onValueChange={(value) => field.onChange(value)}>
                                                       <ButtonGroupItem className="w-full justify-between px-3"
                                                                        value="1" label="1"/>
                                                       <ButtonGroupItem className="w-full justify-between px-3"
                                                                        value="2+" label="2+"/>
                                                   </ButtonGroup>
                                               </FormControl>
                                               <FormMessage>{fieldState.error?.message}</FormMessage>
                                           </FormItem>
                                       )}
                            />

                            {/* Secondo set di pneumatici? */}
                            <FormField control={formRichiesteVeicolo.control} name="reqSetGomme"
                                       render={({field, fieldState}) => (
                                           <FormItem>
                                               <FormLabel>Secondo set di pneumatici?</FormLabel>
                                               <FormControl>
                                                   <ButtonGroup ref={field.ref} value={field.value}
                                                                onValueChange={(value) => field.onChange(value)}>
                                                       <ButtonGroupItem className="w-full justify-between px-3"
                                                                        value="Si" label="Si"/>
                                                       <ButtonGroupItem className="w-full justify-between px-3"
                                                                        value="No" label="No"/>
                                                   </ButtonGroup>
                                               </FormControl>
                                               <FormMessage>{fieldState.error?.message}</FormMessage>
                                           </FormItem>
                                       )}
                            />

                            {/* Libretto di manutenzione */}
                            <FormField control={formRichiesteVeicolo.control} name="reqLibroMan"
                                       render={({field, fieldState}) => (
                                           <FormItem>
                                               <FormLabel>Hai il libretto di manutenzione (o certificato
                                                   digitale)?</FormLabel>
                                               <FormControl>
                                                   <ButtonGroup ref={field.ref} value={field.value}
                                                                onValueChange={(value) => field.onChange(value)}>
                                                       <ButtonGroupItem className="w-full justify-between px-3"
                                                                        value="Si" label="Si"/>
                                                       <ButtonGroupItem className="w-full justify-between px-3"
                                                                        value="No" label="No"/>
                                                   </ButtonGroup>
                                               </FormControl>
                                               <FormMessage>{fieldState.error?.message}</FormMessage>
                                           </FormItem>
                                       )}
                            />

                            {/* Revisione */}
                            <FormField control={formRichiesteVeicolo.control} name="reqRevisione"
                                       render={({field, fieldState}) => (
                                           <FormItem>
                                               <FormLabel>Hai fatto la revisione dell'auto negli ultimi 6
                                                   mesi?</FormLabel>
                                               <FormControl>
                                                   <ButtonGroup columns={3} ref={field.ref} value={field.value}
                                                                onValueChange={(value) => field.onChange(value)}>
                                                       <ButtonGroupItem className="w-full justify-between px-3"
                                                                        value="Si" label="Si"/>
                                                       <ButtonGroupItem className="w-full justify-between px-3"
                                                                        value="No" label="No"/>
                                                       <ButtonGroupItem className="w-full justify-between px-3"
                                                                        value="Scaduta" label="Scaduta"/>
                                                   </ButtonGroup>
                                               </FormControl>
                                               <FormMessage>{fieldState.error?.message}</FormMessage>
                                           </FormItem>
                                       )}
                            />

                            {/* Come vendi */}
                            <FormField control={formRichiesteVeicolo.control} name="reqPrivato"
                                       render={({field, fieldState}) => (
                                           <FormItem>
                                               <FormLabel>Come vendi?</FormLabel>
                                               <FormControl>
                                                   <ButtonGroup ref={field.ref} value={field.value}
                                                                onValueChange={(value) => field.onChange(value)}>
                                                       <ButtonGroupItem className="w-full justify-between px-3"
                                                                        value="Privato" label="Privato"/>
                                                       <ButtonGroupItem className="w-full justify-between px-3"
                                                                        value="Azienda" label="Azienda"/>
                                                   </ButtonGroup>
                                               </FormControl>
                                               <FormMessage>{fieldState.error?.message}</FormMessage>
                                           </FormItem>
                                       )}
                            />

                            {/* L'auto è stata importata */}
                            <FormField control={formRichiesteVeicolo.control} name="reqImportata"
                                       render={({field, fieldState}) => (
                                           <FormItem>
                                               <FormLabel>L'auto è stata importata?</FormLabel>
                                               <FormControl>
                                                   <ButtonGroup ref={field.ref} value={field.value}
                                                                onValueChange={(value) => field.onChange(value)}>
                                                       <ButtonGroupItem className="w-full justify-between px-3"
                                                                        value="Si" label="Si"/>
                                                       <ButtonGroupItem className="w-full justify-between px-3"
                                                                        value="No" label="No"/>
                                                   </ButtonGroup>
                                               </FormControl>
                                               <FormMessage>{fieldState.error?.message}</FormMessage>
                                           </FormItem>
                                       )}
                            />

                            {/* Colore esterno */}
                            <FormField control={formRichiesteVeicolo.control} name="reqColoreEsterno"
                                       render={({field, fieldState}) => (
                                           <FormItem>
                                               <FormLabel>Qual è il colore dell'esterno della tua auto?</FormLabel>
                                               <FormControl>
                                                   <ButtonGroup columns={3} ref={field.ref} value={field.value}
                                                                onValueChange={(value) => field.onChange(value)}>
                                                       <ButtonGroupItem className="w-full justify-between px-3"
                                                                        value="Nero" label="Nero"/>
                                                       <ButtonGroupItem className="w-full justify-between px-3"
                                                                        value="Bianco" label="Bianco"/>
                                                       <ButtonGroupItem className="w-full justify-between px-3"
                                                                        value="Grigio" label="Grigio"/>
                                                       <ButtonGroupItem className="w-full justify-between px-3"
                                                                        value="Blu" label="Blu"/>
                                                       <ButtonGroupItem className="w-full justify-between px-3"
                                                                        value="Rosso" label="Rosso"/>
                                                       <ButtonGroupItem className="w-full justify-between px-3"
                                                                        value="Altro" label="Altro"/>
                                                   </ButtonGroup>
                                               </FormControl>
                                               <FormMessage>{fieldState.error?.message}</FormMessage>
                                           </FormItem>
                                       )}
                            />

                            {/* Colore sedili */}
                            <FormField control={formRichiesteVeicolo.control} name="reqColoreSedili"
                                       render={({field, fieldState}) => (
                                           <FormItem>
                                               <FormLabel>Qual è il colore dei sedili della tua auto?</FormLabel>
                                               <FormControl>
                                                   <ButtonGroup columns={3} ref={field.ref} value={field.value}
                                                                onValueChange={(value) => field.onChange(value)}>
                                                       <ButtonGroupItem className="w-full justify-between px-3"
                                                                        value="Nero" label="Nero"/>
                                                       <ButtonGroupItem className="w-full justify-between px-3"
                                                                        value="Bianco" label="Bianco"/>
                                                       <ButtonGroupItem className="w-full justify-between px-3"
                                                                        value="Grigio" label="Grigio"/>
                                                       <ButtonGroupItem className="w-full justify-between px-3"
                                                                        value="Blu" label="Blu"/>
                                                       <ButtonGroupItem className="w-full justify-between px-3"
                                                                        value="Rosso" label="Rosso"/>
                                                       <ButtonGroupItem className="w-full justify-between px-3"
                                                                        value="Altro" label="Altro"/>
                                                   </ButtonGroup>
                                               </FormControl>
                                               <FormMessage>{fieldState.error?.message}</FormMessage>
                                           </FormItem>
                                       )}
                            />

                            {/* Materiale sedili */}
                            <FormField control={formRichiesteVeicolo.control} name="reqMaterialeSedili"
                                       render={({field, fieldState}) => (
                                           <FormItem>
                                               <FormLabel>Di che materiale sono i sedili?</FormLabel>
                                               <FormControl>
                                                   <ButtonGroup columns={3} ref={field.ref} value={field.value}
                                                                onValueChange={(value) => field.onChange(value)}>
                                                       <ButtonGroupItem className="w-full justify-between px-3"
                                                                        value="Pelle" label="Pelle"/>
                                                       <ButtonGroupItem className="w-full justify-between px-3"
                                                                        value="Tessuto" label="Tessuto"/>
                                                       <ButtonGroupItem className="w-full justify-between px-3"
                                                                        value="Parzialmente in pelle"
                                                                        label="Parzialmente in pelle"/>
                                                       <ButtonGroupItem className="w-full justify-between px-3"
                                                                        value="Materiale sintetico"
                                                                        label="Materiale sintetico"/>
                                                       <ButtonGroupItem className="w-full justify-between px-3"
                                                                        value="Alcantara" label="Alcantara"/>
                                                       <ButtonGroupItem className="w-full justify-between px-3"
                                                                        value="Altro" label="Altro"/>
                                                   </ButtonGroup>
                                               </FormControl>
                                               <FormMessage>{fieldState.error?.message}</FormMessage>
                                           </FormItem>
                                       )}
                            />
                        </form>
                    </Form>
                </>
            )}

            {currentStep === 3 && (
                <>
                    <Form {...formCondizioniVeicolo}>
                        <form id="formCondizioniVeicolo" onSubmit={formCondizioniVeicolo.handleSubmit(onSubmit)}
                              className="grid grid-cols-1 gap-4">

                            {/* L'auto ha mai subito incidenti? */}
                            <FormField control={formCondizioniVeicolo.control} name="reqIncidenti"
                                       render={({field, fieldState}) => (
                                           <FormItem>
                                               <FormLabel>L'auto ha mai subito incidenti?</FormLabel>
                                               <FormControl>
                                                   <ButtonGroup ref={field.ref} value={field.value}
                                                                onValueChange={(value) => field.onChange(value)}>
                                                       <ButtonGroupItem className="w-full justify-between px-3"
                                                                        value="Si" label="Si"/>
                                                       <ButtonGroupItem className="w-full justify-between px-3"
                                                                        value="No" label="No"/>
                                                   </ButtonGroup>
                                               </FormControl>
                                               <FormMessage>{fieldState.error?.message}</FormMessage>
                                           </FormItem>
                                       )}
                            />

                            {/* Stato degli interni */}
                            <FormField control={formCondizioniVeicolo.control} name="reqInterni"
                                       render={({field, fieldState}) => (
                                           <FormItem>
                                               <FormLabel>Qual è lo stato degli interni dell'auto?</FormLabel>
                                               <FormControl>
                                                   <ButtonGroup columns={3} ref={field.ref} value={field.value}
                                                                onValueChange={(value) => field.onChange(value)}>
                                                       <ButtonGroupItem className="w-full justify-between px-3"
                                                                        value="In buone condizioni"
                                                                        label="In buone condizioni"/>
                                                       <ButtonGroupItem className="w-full justify-between px-3"
                                                                        value="Utilizzo moderato"
                                                                        label="Utilizzo moderato"/>
                                                       <ButtonGroupItem className="w-full justify-between px-3"
                                                                        value="Utilizzo intenso"
                                                                        label="Utilizzo intenso"/>
                                                   </ButtonGroup>
                                               </FormControl>
                                               <FormMessage>{fieldState.error?.message}</FormMessage>
                                           </FormItem>
                                       )}
                            />

                            {/* Stato degli esterni */}
                            <FormField control={formCondizioniVeicolo.control} name="reqEsterni"
                                       render={({field, fieldState}) => (
                                           <FormItem>
                                               <FormLabel>Qual è lo stato degli esterni dell'auto?</FormLabel>
                                               <FormControl>
                                                   <ButtonGroup columns={3} ref={field.ref} value={field.value}
                                                                onValueChange={(value) => field.onChange(value)}>
                                                       <ButtonGroupItem className="w-full justify-between px-3"
                                                                        value="In buone condizioni"
                                                                        label="In buone condizioni"/>
                                                       <ButtonGroupItem className="w-full justify-between px-3"
                                                                        value="Utilizzo moderato"
                                                                        label="Utilizzo moderato"/>
                                                       <ButtonGroupItem className="w-full justify-between px-3"
                                                                        value="Utilizzo intenso"
                                                                        label="Utilizzo intenso"/>
                                                   </ButtonGroup>
                                               </FormControl>
                                               <FormMessage>{fieldState.error?.message}</FormMessage>
                                           </FormItem>
                                       )}
                            />

                            {/* Spie luminose */}
                            <FormField control={formCondizioniVeicolo.control} name="reqSpie"
                                       render={({field, fieldState}) => (
                                           <FormItem>
                                               <FormLabel>Ci sono spie luminose accese permanenti sul cruscotto della
                                                   tua auto?</FormLabel>
                                               <FormControl>
                                                   <ButtonGroup ref={field.ref} value={field.value}
                                                                onValueChange={(value) => field.onChange(value)}>
                                                       <ButtonGroupItem className="w-full justify-between px-3"
                                                                        value="Si" label="Si"/>
                                                       <ButtonGroupItem className="w-full justify-between px-3"
                                                                        value="No" label="No"/>
                                                   </ButtonGroup>
                                               </FormControl>
                                               <FormMessage>{fieldState.error?.message}</FormMessage>
                                           </FormItem>
                                       )}
                            />

                            {/* Danni e segni di usura */}
                            <FormField control={formCondizioniVeicolo.control} name="reqUsura"
                                       render={({field, fieldState}) => (
                                           <FormItem>
                                               <FormLabel>Danni e segni di usura</FormLabel>
                                               <FormControl>
                                                   <ButtonGroup ref={field.ref} value={field.value}
                                                                onValueChange={(value) => field.onChange(value)}>
                                                       <ButtonGroupItem className="w-full justify-between px-3"
                                                                        value="Si" label="Si"/>
                                                       <ButtonGroupItem className="w-full justify-between px-3"
                                                                        value="No" label="No"/>
                                                   </ButtonGroup>
                                               </FormControl>
                                               <FormMessage>{fieldState.error?.message}</FormMessage>
                                           </FormItem>
                                       )}
                            />

                            {/* Come si presenta il motore? */}
                            <FormField control={formCondizioniVeicolo.control} name="reqMotore"
                                       render={({field, fieldState}) => (
                                           <FormItem>
                                               <FormLabel>Come si presenta il motore?</FormLabel>
                                               <FormControl>
                                                   <Select {...field} onValueChange={(value) => field.onChange(value)}
                                                           value={field.value}>
                                                       <SelectTrigger className="">
                                                           <SelectValue placeholder="Seleziona lo stato del motore"/>
                                                       </SelectTrigger>
                                                       <SelectContent>
                                                           <SelectItem value="Motore in buone condizioni">Motore in
                                                               buone condizioni</SelectItem>
                                                           <SelectItem value="Motore da riparare">Motore da
                                                               riparare</SelectItem>
                                                       </SelectContent>
                                                   </Select>
                                               </FormControl>
                                               <FormMessage>{fieldState.error?.message}</FormMessage>
                                           </FormItem>
                                       )}
                            />

                            {/* Come si presenta il cambio? */}
                            <FormField control={formCondizioniVeicolo.control} name="reqCambio"
                                       render={({field, fieldState}) => (
                                           <FormItem>
                                               <FormLabel>Come si presenta il cambio?</FormLabel>
                                               <FormControl>
                                                   <Select {...field} onValueChange={(value) => field.onChange(value)}
                                                           value={field.value}>
                                                       <SelectTrigger className="">
                                                           <SelectValue placeholder="Seleziona lo stato del cambio"/>
                                                       </SelectTrigger>
                                                       <SelectContent>
                                                           <SelectItem value="Cambio in buone condizioni">Cambio in
                                                               buone condizioni</SelectItem>
                                                           <SelectItem value="Cambio da riparare">Cambio da
                                                               riparare</SelectItem>
                                                       </SelectContent>
                                                   </Select>
                                               </FormControl>
                                               <FormMessage>{fieldState.error?.message}</FormMessage>
                                           </FormItem>
                                       )}
                            />

                            {/* Come si presenta la frizione? */}
                            <FormField control={formCondizioniVeicolo.control} name="reqFrizione"
                                       render={({field, fieldState}) => (
                                           <FormItem>
                                               <FormLabel>Come si presenta la frizione?</FormLabel>
                                               <FormControl>
                                                   <Select {...field} onValueChange={(value) => field.onChange(value)}
                                                           value={field.value}>
                                                       <SelectTrigger className="">
                                                           <SelectValue
                                                               placeholder="Seleziona lo stato della frizione"/>
                                                       </SelectTrigger>
                                                       <SelectContent>
                                                           <SelectItem value="Frizione in buone condizioni">Frizione in
                                                               buone condizioni</SelectItem>
                                                           <SelectItem value="Frizione da riparare">Frizione da
                                                               riparare</SelectItem>
                                                       </SelectContent>
                                                   </Select>
                                               </FormControl>
                                               <FormMessage>{fieldState.error?.message}</FormMessage>
                                           </FormItem>
                                       )}
                            />

                            {/* Condizionatore */}
                            <FormField control={formCondizioniVeicolo.control} name="reqCondizionatore"
                                       render={({field, fieldState}) => (
                                           <FormItem>
                                               <FormLabel>Il sistema di climatizzazione è in perfette
                                                   condizioni?</FormLabel>
                                               <FormControl>
                                                   <ButtonGroup columns={3} ref={field.ref} value={field.value}
                                                                onValueChange={(value) => field.onChange(value)}>
                                                       <ButtonGroupItem className="w-full justify-between px-3"
                                                                        value="Si" label="Si"/>
                                                       <ButtonGroupItem className="w-full justify-between px-3"
                                                                        value="No" label="No"/>
                                                       <ButtonGroupItem className="w-full justify-between px-3"
                                                                        value="Inesistente" label="Inesistente"/>
                                                   </ButtonGroup>
                                               </FormControl>
                                               <FormMessage>{fieldState.error?.message}</FormMessage>
                                           </FormItem>
                                       )}
                            />
                        </form>
                    </Form>
                </>
            )}

            {currentStep === 4 && (
                <>
                    <Form {...formDatiCliente}>
                        <form id="formDatiCliente" onSubmit={formDatiCliente.handleSubmit(onSubmit)}
                              className="grid grid-cols-2 gap-4">

                            {/* Nome */}
                            <FormField control={formDatiCliente.control} name="nome"
                                       render={({field, fieldState}) => (
                                           <FormItem>
                                               <FormLabel>Nome</FormLabel>
                                               <FormControl>
                                                   <Input {...field} placeholder="Inserisci il nome"/>
                                               </FormControl>
                                               <FormMessage>{fieldState.error?.message}</FormMessage>
                                           </FormItem>
                                       )}
                            />

                            {/* Cognome */}
                            <FormField control={formDatiCliente.control} name="cognome"
                                       render={({field, fieldState}) => (
                                           <FormItem>
                                               <FormLabel>Cognome</FormLabel>
                                               <FormControl>
                                                   <Input {...field} placeholder="Inserisci il cognome"/>
                                               </FormControl>
                                               <FormMessage>{fieldState.error?.message}</FormMessage>
                                           </FormItem>
                                       )}
                            />

                            {/* Telefono */}
                            <FormField control={formDatiCliente.control} name="telefono"
                                       render={({field, fieldState}) => (
                                           <FormItem>
                                               <FormLabel>Telefono</FormLabel>
                                               <FormControl>
                                                   <Input {...field} placeholder="Inserisci il numero di telefono"/>
                                               </FormControl>
                                               <FormMessage>{fieldState.error?.message}</FormMessage>
                                           </FormItem>
                                       )}
                            />

                            {/* Provincia */}
                            <FormField control={formDatiCliente.control} name="provincia"
                                       render={({field, fieldState}) => (
                                           <FormItem>
                                               <FormLabel>Provincia</FormLabel>
                                               <FormControl>
                                                   <Select {...field}
                                                           onValueChange={(value) => field.onChange(value)}
                                                           value={field.value}>
                                                       <SelectTrigger
                                                           className="">
                                                           <SelectValue
                                                               placeholder="Seleziona la provincia"/>
                                                       </SelectTrigger>
                                                       <SelectContent>
                                                           {provinces.map((province) => (
                                                               <SelectItem
                                                                   key={province.code}
                                                                   value={province.code}>
                                                                   {province.name}
                                                               </SelectItem>
                                                           ))}
                                                       </SelectContent>
                                                   </Select>
                                               </FormControl>
                                               <FormMessage>{fieldState.error?.message}</FormMessage>
                                           </FormItem>
                                       )}
                            />

                            {/* Targa */}
                            <FormField control={formDatiCliente.control} name="targa"
                                       render={({field, fieldState}) => (
                                           <FormItem>
                                               <FormLabel>Targa</FormLabel>
                                               <FormControl>
                                                   <Input {...field} placeholder="Es. AB123CD"/>
                                               </FormControl>
                                               <FormMessage>{fieldState.error?.message}</FormMessage>
                                           </FormItem>
                                       )}
                            />

                            {/* Valutazione */}
                            <FormField control={formDatiCliente.control} name="valutazione"
                                       render={({field, fieldState}) => {
                                           const permuta = formDettagliGenerali.watch("permuta"); // Usa watch per ottenere il valore
                                           const label = permuta === "Permuta"
                                               ? "Quanto desideri ricavare dalla permuta?"
                                               : "A quanto vuoi vendere la tua auto?";
                                           const placeholder = permuta === "Permuta"
                                               ? "Quanto desideri ricavare?"
                                               : "A quanto vuoi vendere?";

                                           return (
                                               <FormItem>
                                                   <FormLabel>{label}</FormLabel>
                                                   <FormControl>
                                                       <Input {...field} placeholder={placeholder}/>
                                                   </FormControl>
                                                   <FormMessage>{fieldState.error?.message}</FormMessage>
                                               </FormItem>
                                           );
                                       }}
                            />


                            {/* Note */}
                            <FormField control={formDatiCliente.control} name="note"
                                       render={({field, fieldState}) => (
                                           <FormItem className="col-span-2">
                                               <FormLabel>Note</FormLabel>
                                               <FormControl>
                                                   <Textarea {...field} placeholder="Aggiungi eventuali note"/>
                                               </FormControl>
                                               <FormMessage>{fieldState.error?.message}</FormMessage>
                                           </FormItem>
                                       )}
                            />
                        </form>
                    </Form>
                </>
            )}

            {currentStep === 5 && (
                <>
                    <div>
                        <div className="space-y-4">

                            {/*<h3 className="text-xl text-center font-semibold text-gray-800">
                                {valutezione?.min && valutezione?.max ? (
                                    <>
                                        La valutazione stimata della tua auto è tra{' '}
                                        <span className="text-green-600 font-bold">
        {formatPrice(valutezione.min)}
      </span>{' '}
                                        e{' '}
                                        <span className="text-green-600 font-bold">
        {formatPrice(valutezione.max)}
      </span>
                                    </>
                                ) : (
                                    <span className="text-red-600">
      Non siamo riusciti a stimare il valore della tua auto. <br/>
      <span className="font-medium text-gray-800">
        Ti aspettiamo in sede per una valutazione personalizzata.
      </span>
    </span>
                                )}
                            </h3>*/}


                            <CardDescription className="text-lg">Aggiungi foto per una valutazione più rapida e
                                precisa</CardDescription>

                            <Alert variant={'destructive'}>
                                <AlertCircle className="h-4 w-4"/>
                                <AlertTitle>Attenzione</AlertTitle>
                                <AlertDescription>
                                    Assicurati che le foto siano chiare e ben visibili. Il numero minimo di foto
                                    richieste è {minNumberOfPhotos}.
                                </AlertDescription>
                            </Alert>

                            <div className="grid grid-cols-1 gap-4">
                                <div
                                    {...getRootProps()}
                                    className={`border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors ${
                                        isDragActive ? "border-primary bg-primary/10" : "border-gray-300 hover:border-primary"
                                    }`}
                                >
                                    <input {...getInputProps()} />
                                    <Upload className="mx-auto h-12 w-12 text-gray-400"/>
                                    <p className="mt-2 text-sm text-gray-600">Trascina qui le tue foto o clicca per
                                        selezionarle</p>
                                    <p className="text-xs text-gray-500">(Minimo {minNumberOfPhotos} foto, formati accettati: JPG,
                                        PNG)</p>
                                </div>

                                {photos.length > 0 && (
                                    <div>
                                        <h3 className="text-lg font-semibold mb-3">
                                            Foto caricate ({photos.length}/{minNumberOfPhotos}):
                                        </h3>
                                        <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-4">
                                            {photos.map((photo, index) => (
                                                <div key={index} className="relative group">
                                                    <div className="aspect-square relative overflow-hidden rounded-lg flex items-center justify-center">
                                                        {photo.isLoading ? (
                                                            <Loader className="h-6 w-6 text-gray-400 animate-spin"/>
                                                        ) : (
                                                            <Image
                                                                src={photo.url || ""}
                                                                alt={`Foto auto ${index + 1}`}
                                                                fill={true}
                                                                className="object-cover"
                                                            />
                                                        )}
                                                    </div>
                                                    {!photo.isLoading && (
                                                        <button
                                                            onClick={() => removePhoto(index)}
                                                            className="absolute top-1 right-1 bg-red-500 text-white p-1 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                                                        >
                                                            <X className="h-4 w-4"/>
                                                        </button>
                                                    )}
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}

                            </div>
                        </div>

                    </div>
                </>
            )}

            <div className="flex justify-between mt-4 col-span-2">
                <div className="flex flex-col justify-center items-center w-full">
                    {currentStep < 5 ? (
                            <Button onClick={nextStep} disabled={isSubmitting}
                                    className="sm:min-w-64 max-sm:w-[80%] mt-10 text-white bg-[#D73133] hover:bg-[#D73133]/90">
                                Vai all’offerta
                                <ChevronRight className="h-5 w-5 ml-2"/>
                            </Button>
                        )
                        :
                        (
                            <Button
                                onClick={onSubmit}
                                disabled={isSubmitting || isSubmittingRef.current || photos.some(photo => photo.isLoading) || !productId || !permutaId}
                                className="bg-[#D73133] hover:bg-[#D73133]/90 sm:min-w-64 text-white bg-gradient-custom"
                            >
                                {isSubmitting ? 'Invio...' : 'Invia richiesta'}
                            </Button>

                        )
                    }
                    {
                        currentStep > 1 && (
                            <Button onClick={prevStep}
                                    className="text-gray-500 bg-transparent underline hover:bg-transparent">indietro</Button>
                        )
                    }
                </div>
            </div>

            <Dialog open={openDialog} onOpenChange={setOpenDialog}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Attenzione</DialogTitle>
                        <DialogDescription>{dialogMessage}</DialogDescription>
                    </DialogHeader>
                    <DialogFooter>
                        <Button onClick={() => setOpenDialog(false)}>Chiudi</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </section>
    )

};

export default FormPermuta;
